import * as React from "react";
import { ActorRef } from "@/services/bedrock/dtos/DesignPackage";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";

/**
 * The person at the keyboard, as the design endpoints record them. While impersonating, bedrock puts
 * the real person in `actor` and the account in `actor.onBehalfOf`, but the session only knows the
 * account's user id, so an impersonator is recognized by the account id plus their own name.
 */
export interface Viewer {
  userId: unknown;
  name?: string | null;
  /** The real person's name while impersonating; absent otherwise. */
  impersonatorName?: string | null;
}

export function isSelfActor(
  actor: Pick<ActorRef, "userId" | "name" | "onBehalfOf"> | null | undefined,
  viewer: Viewer
) {
  if (!actor || viewer.userId == null) return false;
  if (viewer.impersonatorName) {
    return Number(actor.onBehalfOf?.userId) === Number(viewer.userId) && actor.name === viewer.impersonatorName;
  }
  return Number(actor.userId) === Number(viewer.userId);
}

/** How the viewer's own optimistic entries are attributed, so they read like the server's. */
export function viewerActor(viewer: Viewer): ActorRef {
  if (viewer.impersonatorName) {
    return {
      userId: Number(viewer.userId),
      name: viewer.impersonatorName,
      avatarUrl: null,
      onBehalfOf: { userId: Number(viewer.userId), name: viewer.name ?? "" },
    };
  }
  return { userId: Number(viewer.userId), name: viewer.name || "You", avatarUrl: null, onBehalfOf: null };
}

export function useViewer(): Viewer {
  const { session, userId, fullName } = useSocialCoachSession();
  const impersonatorName = session?.actor?.fullName ?? null;
  return React.useMemo(() => ({ userId, name: fullName, impersonatorName }), [userId, fullName, impersonatorName]);
}
